// ShareRecipeButton.js
import React, { useState, useContext } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Box,
  Avatar,
  Typography,
  Paper,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FacebookIcon from '@mui/icons-material/Facebook';
import ThreadsIcon from "../assets/images/threads-logo-black-01.png";
import XIcon from '@mui/icons-material/X';
import EmailIcon from '@mui/icons-material/Email';
import { AuthContext } from '../context/AuthContext';
import { axiosInstance } from '../axiosInstance';

const ShareRecipeButton = ({ recipeId, recipeTitle, recipeContent }) => {
  const { accessToken } = useContext(AuthContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const ensureShareUrl = async () => {
    if (shareUrl) return shareUrl;
    try {
      const payload = { recipe: recipeContent };
      const response = await axiosInstance.post('/api/shared/shared-recipes', payload);
      const url = response.data.url;
      setShareUrl(url);
      return url;
    } catch (err) {
      console.error('Failed to generate share link:', err);
      setError('Failed to generate share link.');
      throw err;
    }
  };

  const handleCopy = async () => {
    try {
      const url = await ensureShareUrl();
      const fullUrl = `${window.location.origin}${new URL(url).pathname}`;
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      handleClose();
    } catch {}
  };

  const openInNewTab = async (urlBuilder, useMeta = false) => {
    try {
      const url = await ensureShareUrl();
      const fullUrl = new URL(url, window.location.origin);
      // Here instead of the React app's base origin URL, we target the back-end API URL
      // set in axiosInstance
      const targetUrl = useMeta
        ? `${axiosInstance.defaults.baseURL}/api/shared/shared-recipes${fullUrl.pathname.replace('/shared/recipe', '')}/meta`
        : fullUrl.href;
  
      const shareLink = urlBuilder(
        encodeURIComponent(recipeTitle || 'Check out this recipe!'),
        encodeURIComponent(targetUrl)
      );
  
      window.open(shareLink, '_blank');
      handleClose();
    } catch {}
  };
  

  const renderPreviewCard = () => (
    <Paper sx={{ display: 'flex', alignItems: 'center', p: 1, backgroundColor: '#f5f5f5' }}>
      <Avatar
        src={recipeContent.imageUrl}
        alt={recipeTitle}
        sx={{ width: 56, height: 56, mr: 2 }}
        variant="rounded"
      />
      <Box>
        <Typography variant="subtitle2" noWrap>
          {recipeTitle}
        </Typography>
        <Typography variant="body2" color="textSecondary" noWrap>
          {recipeContent.introduction || 'Link copied to clipboard!'}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <>
      <Tooltip title="Share Recipe">
        <IconButton onClick={handleOpen} aria-label="share">
          <ShareIcon color="success" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleCopy}>
          <ContentCopyIcon fontSize="small" style={{ marginRight: 8 }} />
          Copy Link
        </MenuItem>
        <MenuItem onClick={() => openInNewTab((title, url) => `mailto:?subject=${title}&body=${url}`, true)}>
          <EmailIcon fontSize="small" style={{ marginRight: 8 }} />
          Share via Email
        </MenuItem>
        <MenuItem onClick={() => openInNewTab((title, url) => `https://x.com/intent/tweet?text=${title}&url=${url}`, true)}>
          <XIcon fontSize="small" style={{ marginRight: 8 }} />
          Share on X
        </MenuItem>
        <MenuItem onClick={() => openInNewTab((_, url) => `https://www.facebook.com/sharer/sharer.php?u=${url}`, true)}>
          <FacebookIcon fontSize="small" style={{ marginRight: 8 }} />
          Share on Facebook
        </MenuItem>
        <MenuItem onClick={() => openInNewTab((title, url) => `https://www.threads.net/intent/post?text=${title}%20${url}`, true)}>
          <img src={ThreadsIcon} alt="Threads" style={{ width: 18, marginRight: 8 }} />
          Share on Threads
        </MenuItem>
      </Menu>
      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        message={renderPreviewCard()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{ sx: { backgroundColor: 'transparent', boxShadow: 'none' } }}
      />
      <Snackbar open={Boolean(error)} autoHideDuration={3000} message={error} onClose={() => setError(null)} />
    </>
  );
};

export default ShareRecipeButton;